import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./../App.css";
import logo from "./../assets/img/Logo.png";
import search from "./../assets/img/search.svg";
import profile from "./../assets/img/profile.svg";
import cart from "./../assets/img/cart.svg";
import {
  home_search,
  get_cart_count,
  guest_get_cart_count,
} from "../service/api.js";
import { getHeaderMenu, getHeaderPortals, pickMenu, readHeaderMenu, readHeaderPortals } from "../service/headerCache.js";
import { BRANDING_SOLUTIONS } from "./constants.js";
import Modal from "./Modal.js";
import CartModel from "./CartModel.js";
import { createPortal } from "react-dom";

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── UI state ───────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [submenuStates, setSubmenuStates] = useState({});
  const savedMenu = readHeaderMenu();

  // ── Search ─────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [searchArray, setSearchArray] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // ── Menu data ──────────────────────────────────────────────────
  const [menuItems2, setMenuItems2] = useState(savedMenu || []);
  const [menuItemsapperal, setMenuItemsapperal] = useState(pickMenu(savedMenu, "apparel"));
  const [MenuItemscollection, setMenuItemscollection] = useState(pickMenu(savedMenu, "collections"));
  const [menuItemsbrand, setmenuItemsbrand] = useState(pickMenu(savedMenu, "brands"));
  const [portalItems, setportalItems] = useState(readHeaderPortals() || []);

  // ── Cart ───────────────────────────────────────────────────────
  const [cartData, setCartData] = useState(
    parseInt(localStorage.getItem("cartItemCount")) || 0
  );

  const [openCartModal, setOpenCartModal] = useState(false);

  // ── Branding Solutions modal ───────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({});

  const isAuthenticated = !!localStorage.getItem("token");

  // ── Resize listener ────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setShowResults(false);
    setSearchTerm("");
  }, [location.pathname]);

  // Menu and portals come from localStorage. The API runs only when that copy is missing.
  useEffect(() => {
    let active = true;
    getHeaderMenu()
      .then((data) => {
        if (!active) return;
        setMenuItemsapperal(pickMenu(data, "apparel"));
        setmenuItemsbrand(pickMenu(data, "brands"));
        setMenuItemscollection(pickMenu(data, "collections"));
        setMenuItems2(data);
      })
      .catch((error) => {
        console.error("Error fetching category items", error);
      });

    if (!localStorage.getItem("token")) return () => { active = false; };
    getHeaderPortals()
      .then((data) => {
        if (active) setportalItems(data);
      })
      .catch((error) => {
        console.error("Error fetching portal list", error);
      });

    return () => { active = false; };
  }, []);

  // Cart count is already kept in localStorage by add-to-cart. Ask the API only the first time.
  useEffect(() => {
    if (localStorage.getItem("cartItemCount") !== null) return;
    let active = true;
    const loadCartCount = async () => {
      try {
        const loggedIn = !!localStorage.getItem("token");
        const res = loggedIn ? await get_cart_count() : await guest_get_cart_count();
        if (!active || res.status !== 200) return;
        setCartData(res.data.CartCount);
        localStorage.setItem("cartItemCount", res.data.CartCount);
      } catch (error) {
        console.error("Error fetching cart count:", error);
      }
    };
    loadCartCount();
    return () => { active = false; };
  }, []);

  // Open the same cart sidebar (mini cart) that appears after "Add to cart"
  const handleCartIconClick = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    setOpenCartModal(true);
  };

  // ── Search ─────────────────────────────────────────────────────
  const handleSearchChange = async (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    try {
      const res = await home_search({ Search: term });
      setSearchArray(res.data.Data);
      setShowResults(true);
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") navigate(`/search/${searchTerm}`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const el = document.querySelector(".search-container");
      if (el && !el.contains(event.target)) setShowResults(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // ── Mobile submenu toggle ──────────────────────────────────────
  const toggleSubMenu = (key) => {
    setSubmenuStates((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Branding Solutions modal ───────────────────────────────────
  const handleShowDetail = (id) => {
    const item = BRANDING_SOLUTIONS.find((i) => Number(i.id) === Number(id));
    setModalData(item);
    setShowModal(true);
  };

  // ── Auth ───────────────────────────────────────────────────────
  const handleSignInClick = () =>
    localStorage.setItem("lastVisitedPage", location.pathname);

  const logout = () => {
    localStorage.clear();
    navigate("/signin");
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <header>
      <Box
        component="div"
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        className="container"
      >
        {/* Logo */}
        <div className="header-logo">
          <Link to="/">
            <img src={logo} alt="logo" />
          </Link>
        </div>

        {/* ── Desktop: auth icons only (nav is in category-header.js) ── */}
        {!isMobile && (
          <nav>
            <Box
              component="ul"
              sx={{ display: "flex", alignItems: "center" }}
              className="header-ul"
            >
              {isAuthenticated ? (
                <>
                  <Box
                    component="li"
                    onClick={logout}
                    sx={{ display: "flex", alignItems: "center" }}
                    className="call-li"
                    style={{ cursor: "pointer", marginLeft: "0.5vw", marginRight: "1vw" }}
                  >
                    Logout
                  </Box>
                  <li>
                    <Link to="/current_orders">
                      <div className="profile-icon">
                        <img src={profile} alt="Profile" />
                      </div>
                    </Link>
                  </li>
                </>
              ) : (
                <Link to="/signin" onClick={handleSignInClick}>
                  <Box
                    component="li"
                    sx={{ display: "flex", alignItems: "center" }}
                    className="header_signup_button"
                  >
                    Sign In
                  </Box>
                </Link>
              )}
              <li>
                <Link to="/cart" onClick={handleCartIconClick}>
                  <div className="cart-icon">
                    <img src={cart} alt="Cart" />
                    <span className="cart-badge">{cartData}</span>
                  </div>
                </Link>
              </li>
            </Box>
          </nav>
        )}

        {/* ── Mobile: hamburger + slide-out menu ─────────────────── */}
        {isMobile && (
          <>
            <MenuIcon onClick={() => setIsMenuOpen(true)} />

            <div className={`mobile_menu_wrapper ${isMenuOpen ? "active" : ""}`}>
              {/* Close button */}
              <div className="mobilemenu_close_icon">
                <CloseIcon onClick={() => setIsMenuOpen(false)} />
              </div>

              {/* Search */}
              <div className="search-container">
                <div className="search-feild">
                  <input
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyPress={handleKeyPress}
                  />
                  <img src={search} className="search-icon" alt="search" />
                </div>
                {searchTerm && (
                  <div
                    className="search-results"
                    style={{ display: showResults ? "block" : "none" }}
                  >
                    {searchArray.Product?.length === 0 &&
                    searchArray.SubCategory?.length === 0 ? (
                      <div>No results found.</div>
                    ) : (
                      <ul>
                        {searchArray.Product?.length > 0 && (
                          <li>
                            <strong>Product</strong>
                            <ul style={{ overflow: "auto", height: "auto" }}>
                              {searchArray.Product.map((product, index) => (
                                <li key={index} style={{ width: "33.33%" }}>
                                  <Link
                                    to={`/product/${product.Slug}`}
                                    style={{
                                      color: "#01AEF0",
                                      fontWeight: "600",
                                      width: "100%",
                                      fontSize: "14px",
                                    }}
                                  >
                                    <img
                                      src={
                                        process.env.REACT_APP_API_URL +
                                        product.ProductImage
                                      }
                                      style={{ width: "100%", height: "auto" }}
                                      alt={product.ProductName}
                                    />
                                    {product.ProductName}
                                    <br />
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        color: "rgb(37,37,37)",
                                        fontWeight: "500",
                                        position: "relative",
                                        top: "5px",
                                      }}
                                    >
                                      SKU : {product.SKU}
                                    </span>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Navigation — mirrors desktop category-header.js */}
              <nav>
                <Box
                  component="ul"
                  sx={{ display: "flex", alignItems: "center" }}
                  className="cate-header-ul"
                >
                  {/* Products */}
                  <li className="all_products">
                    <Link to="/shop" onClick={() => toggleSubMenu("products")}>
                      Products
                    </Link>
                    <Box
                      component="ul"
                      sx={{ display: "flex", alignItems: "center" }}
                      className={`all_products_1 hover_open_menu ${
                        submenuStates["products"] ? "open" : ""
                      }`}
                    >
                      {menuItems2.map((menuItem, index) =>
                        menuItem.Slug !== "apparel" &&
                        menuItem.Slug !== "brands" &&
                        menuItem.Slug !== "oztag" &&
                        menuItem.Slug !== "merchandise" &&
                        menuItem.Slug !== "collections" ? (
                          <li key={index} className="sub_cate_menu_">
                            <Link to={`/category/${menuItem.Slug}`}>
                              {menuItem.CategoryName}
                              <svg
                                height="16"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M8.25 4.5l7.5 7.5-7.5 7.5"
                                />
                              </svg>
                            </Link>
                            <Box
                              component="ul"
                              sx={{ display: "flex", alignItems: "center" }}
                              className="all_products_2 hover_open_menu_1"
                            >
                              {menuItem.SubCategory.map((childmenu, idx2) => (
                                <li key={idx2}>
                                  <Link to={`/${menuItem.Slug}/${childmenu.Slug}`}>
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: childmenu.SubCategoryName,
                                      }}
                                    />
                                  </Link>
                                </li>
                              ))}
                            </Box>
                          </li>
                        ) : null
                      )}
                    </Box>
                  </li>

                  {/* Signage */}
                  <li>
                    <Link to="/print/signage">Signage</Link>
                  </li>

                  {/* Apparel */}
                  <li className="all_products">
                    {menuItemsapperal.SubCategory?.length === 0 ? (
                      <Link to={`/category/${menuItemsapperal.Slug}`}>Apparel</Link>
                    ) : (
                      <>
                        <Link
                          to="#"
                          onClick={(e) => { e.preventDefault(); toggleSubMenu("apparel"); }}
                        >
                          Apparel
                        </Link>
                        <ul
                          className={`child_menu cate-header-ul all_products_1 hover_open_menu portal apparel_ ${
                            submenuStates["apparel"] ? "open" : ""
                          }`}
                          style={{ marginTop: 0 }}
                        >
                          {menuItemsapperal.SubCategory?.map((menuItem, index) => (
                            <li key={index} className="sub_cate_menu_ apparel">
                              <Link
                                to={`/${menuItemsapperal.Slug}/${menuItem.Slug}`}
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: menuItem.SubCategoryName,
                                  }}
                                />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </li>

                  {/* New */}
                  <li>
                    <Link to="/products/new">New</Link>
                  </li>

                  {/* Brand */}
                  {menuItemsbrand.SubCategory && (
                    <li className="all_products">
                      {menuItemsbrand.SubCategory.length === 0 ? (
                        <Link to={`/category/${menuItemsbrand.Slug}`}>Brand</Link>
                      ) : (
                        <>
                          <Link
                            to="#"
                            onClick={(e) => { e.preventDefault(); toggleSubMenu("brand"); }}
                          >
                            Brand
                          </Link>
                          <ul
                            className={`child_menu cate-header-ul all_products_1 hover_open_menu portal Brand_ ${
                              submenuStates["brand"] ? "open" : ""
                            }`}
                            style={{ marginTop: 0 }}
                          >
                            {menuItemsbrand.SubCategory.map((menuItem, index) => (
                              <li key={index} className="sub_cate_menu_ apparel">
                                <Link
                                  to={`/${menuItemsbrand.Slug}/${menuItem.Slug}`}
                                >
                                  <img
                                    src={
                                      process.env.REACT_APP_API_URL +
                                      menuItem.SubCategoryImage
                                    }
                                    alt={menuItem.SubCategoryName}
                                  />
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: menuItem.SubCategoryName,
                                    }}
                                  />
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </li>
                  )}

                  {/* Collections */}
                  {MenuItemscollection.SubCategory && (
                    <li className="all_products">
                      {MenuItemscollection.SubCategory.length === 0 ? (
                        <Link to={`/category/${MenuItemscollection.Slug}`}>
                          Collections
                        </Link>
                      ) : (
                        <>
                          <Link
                            to="#"
                            onClick={(e) => { e.preventDefault(); toggleSubMenu("collections"); }}
                          >
                            Collections
                          </Link>
                          <ul
                            className={`child_menu cate-header-ul all_products_1 hover_open_menu portal collections_ ${
                              submenuStates["collections"] ? "open" : ""
                            }`}
                            style={{ marginTop: 0 }}
                          >
                            {MenuItemscollection.SubCategory.map(
                              (menuItem, index) => (
                                <li key={index} className="sub_cate_menu_ apparel">
                                  <Link
                                    to={`/${MenuItemscollection.Slug}/${menuItem.Slug}`}
                                  >
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: menuItem.SubCategoryName,
                                      }}
                                    />
                                  </Link>
                                </li>
                              )
                            )}
                          </ul>
                        </>
                      )}
                    </li>
                  )}

                  {/* Sale */}
                  <li>
                    <Link to="/products/sale">Sale</Link>
                  </li>

                  {/* Portals (logged-in only) */}
                  {isAuthenticated && portalItems.length > 0 && (
                    <li className="all_products" style={{ cursor: "pointer" }}>
                      {portalItems.length === 1 ? (
                        <Link to={`/${portalItems[0].Slug}`}>
                          {portalItems[0].PortalName}
                        </Link>
                      ) : (
                        <>
                          <Link to="#" onClick={() => toggleSubMenu("portals")}>
                            Portals
                          </Link>
                          <ul
                            className={`child_menu cate-header-ul all_products_1 hover_open_menu portal ${
                              submenuStates["portals"] ? "open" : ""
                            }`}
                            style={{ marginTop: 0 }}
                          >
                            {portalItems.map((menuItem, index) => (
                              <li key={index} className="sub_cate_menu_">
                                <Link to={`/${menuItem.Slug}`}>
                                  {menuItem.PortalName}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </li>
                  )}

                  {/* Branding Solutions */}
                  {BRANDING_SOLUTIONS.length > 0 && (
                    <li className="all_products" style={{ cursor: "pointer" }}>
                      {BRANDING_SOLUTIONS.length === 1 ? (
                        <Link onClick={() => handleShowDetail(BRANDING_SOLUTIONS[0].id)}>
                          {BRANDING_SOLUTIONS[0].title}
                        </Link>
                      ) : (
                        <>
                          <Link
                            to="#"
                            onClick={() => toggleSubMenu("brandingsol")}
                          >
                            Branding Solutions
                          </Link>
                          <ul
                            className={`child_menu cate-header-ul all_products_1 hover_open_menu portal Brand_ ${
                              submenuStates["brandingsol"] ? "open" : ""
                            }`}
                            style={{ marginTop: 0 }}
                          >
                            {BRANDING_SOLUTIONS.map((menuItem, index) => (
                              <li
                                key={index}
                                className="sub_cate_menu_ apparel branding"
                              >
                                <Link onClick={() => handleShowDetail(menuItem.id)}>
                                  <img
                                    src={menuItem.image}
                                    style={{
                                      width: "4rem",
                                      height: "4rem",
                                      marginRight: "5px",
                                    }}
                                    alt={menuItem.title}
                                  />
                                  {menuItem.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </li>
                  )}

                  {/* Blog */}
                  <li className="all_products" style={{ cursor: "pointer" }}>
                    <Link to="https://blog.bebranded.com.au">Blog</Link>
                  </li>

                  {/* OZTAG */}
                  <li className="all_products" style={{ cursor: "pointer" }}>
                    <Link to="/oztag/oztag">OZTAG</Link>
                  </li>

                  {/* Logout — in nav list when authenticated */}
                  {isAuthenticated && (
                    <li style={{ cursor: "pointer" }}>
                      <Link
                        to="#"
                        onClick={(e) => { e.preventDefault(); logout(); }}
                        style={{ color: "#e53935", fontWeight: 500 }}
                      >
                        Logout
                      </Link>
                    </li>
                  )}

                  {/* Sign In — in nav list when not authenticated */}
                  {!isAuthenticated && (
                    <li>
                      <Link to="/signin" onClick={handleSignInClick}
                        className="header_signup_button"
                        style={{ display: "inline-block" }}
                      >
                        Sign In
                      </Link>
                    </li>
                  )}
                </Box>

                {/* Profile icon + cart — always shown below the nav list */}
                <Box
                  component="ul"
                  sx={{ display: "flex", alignItems: "center", gap: "16px" }}
                  className="header-ul"
                  style={{ padding: "12px 0 4px" }}
                >
                  {isAuthenticated && (
                    <li>
                      <Link to="/current_orders">
                        <div className="profile-icon">
                          <img src={profile} alt="Profile" />
                        </div>
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link to="/cart" onClick={handleCartIconClick}>
                      <div className="cart-icon">
                        <img src={cart} alt="Cart" />
                        <span className="cart-badge">{cartData}</span>
                      </div>
                    </Link>
                  </li>
                </Box>
              </nav>
            </div>
          </>
        )}
      </Box>

      {/* Cart sidebar (mini cart) — rendered in a portal so it sits above the page */}
      {openCartModal &&
        createPortal(
          <div>
            <CartModel
              openCartModal={openCartModal}
              setOpenCartModal={setOpenCartModal}
              onCartCountChange={setCartData}
            />
          </div>,
          document.body
        )}

      {/* Branding Solutions modal */}
      {showModal && (
        <Modal modalOpen={showModal} setModalOpen={setShowModal}>
          <div className="modal_wrapper">
            <span className="modal_closer" onClick={() => setShowModal(false)}>
              &times;
            </span>
            <div className="modal_title_wrapper">
              <h3>{modalData?.title}</h3>
            </div>
            <div className="modal_image">
              <img src={modalData.modal_img} style={{ width: "100%" }} alt={modalData?.title} />
            </div>
            <p className="modal_txt" style={{ marginTop: "7px" }}>
              {modalData?.Description}
            </p>
            {modalData.Description2 && (
              <p className="modal_txt" style={{ marginTop: "7px" }}>
                {modalData?.Description2}
              </p>
            )}
            <div style={{ marginTop: "7px" }}>
              <h3>Advantages</h3>
              <ul className="modal_list_wrapper">
                {modalData.Advantages_list?.map((listdata) => (
                  <li key={listdata.id} className="modal_txt">
                    {listdata.data}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ marginTop: "7px" }}>
              <h3>Limitations</h3>
              <ul className="modal_list_wrapper">
                {modalData.Limitations_list?.map((listdata) => (
                  <li key={listdata.id} className="modal_txt">
                    {listdata.data}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ marginTop: "7px" }}>
              <h3>Artwork Requirements</h3>
              <ul className="modal_list_wrapper">
                {modalData.Artwork_requirements_list?.map((listdata) => (
                  <React.Fragment key={listdata.id}>
                    <li className="modal_txt">{listdata.data}</li>
                    {listdata.sub_data_list?.length > 0 && (
                      <ul
                        style={{
                          marginLeft: "30px",
                          listStyle: "disc",
                          marginTop: "6px",
                        }}
                      >
                        {listdata.sub_data_list.map((item) => (
                          <li key={item.id} className="modal_txt">
                            {item.sub_data}
                          </li>
                        ))}
                      </ul>
                    )}
                  </React.Fragment>
                ))}
              </ul>
            </div>
          </div>
        </Modal>
      )}
    </header>
  );
};
